<template>
  <div class="text-center floating-btn">
    <v-btn
      v-if="showFeedbackButton"
      id="bbh-floating-btn-feedback"
      class="bbh-floating-btn-feedback"
      fab
      color="var(--bbh-blue)"
      @click.exact="showFeedbackForm"
      @click.shift="consoleLogVersion"
    >
      <v-icon>$messagesPeoplePersonBubbleSquare1</v-icon>
    </v-btn>
    <v-menu
      top
      offset-y
      content-class="bbh-floating-btn-menu-wrapper"
    >
      <template #activator="{ on, attrs }">
        <v-btn
          id="bbh-floating-btn"
          fab
          v-bind="attrs"
          color="var(--bbh-red)"
          v-on="on"
          @click="$emit('click')"
          @click.shift="consoleLogVersion"
        >
          <v-icon> $bbhLogo </v-icon>
        </v-btn>
      </template>
      <div id="bbh-floating-btn-menu">
        <v-btn
          v-for="(item, index) in items"
          :key="index"
          icon
          @click="handleIconClicked(item.icon)"
        >
          <bbh-tooltip
            :bottom="true"
            :open-delay="500"
          >
            <template #activator>
              <v-icon>
                {{ item.icon }}
              </v-icon>
            </template>
            <span>{{ item.text }}</span>
          </bbh-tooltip>
        </v-btn>
      </div>
    </v-menu>
    <DefaultDialog
      header=""
      :open-modal="openFeedbackForm"
      center-header
      scrollable
      @cancel="handleFeedbackDialogDismiss"
      @close="handleFeedbackDialogDismiss"
    >
      <template #content>
        <iframe
          :key="feedbackFormFrameKey"
          class="template-editor-feedback-form"
          width="640px"
          height="480px"
          :src="feedbackFormUrl"
          frameborder="0"
          marginwidth="0"
          marginheight="0"
          allowfullscreen
          webkitallowfullscreen
          mozallowfullscreen
          msallowfullscreen
          @load="handleFeedbackFormLoad"
        />
      </template>
    </DefaultDialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import types from '../../store/types';
import { version } from '../../../package.json';
import DefaultDialog from '../dialogs/DefaultDialog.vue';

const userGetters = types.getters.user;
const viewContentMutations = types.mutations.viewContent;
const feedbackFormSubmittedStorageKey = 'bbh-template-editor-feedback-form-submitted';
const feedbackFormDismissedStorageKey = 'bbh-template-editor-feedback-form-dismissed';
const feedbackButtonPulseClass = 'bbh-floating-btn-feedback--pulse';
const feedbackButtonPulseDuration = 5000;
const feedbackFormUrl =
  'https://forms.office.com/Pages/ResponsePage.aspx?id=NXh_Y48Hy0aKDXzX6S00I_8SvPyRlCpIpgEt2T_WyV5UMUJVT1MyNjg5QkRIV1lMWlJSVUxPWFM0Ti4u&embed=true';

export default {
  name: 'FloatingButton',
  components: {
    DefaultDialog,
  },
  data: () => ({
    items: [
      { icon: '$phoneAlt', text: 'Anruf anfordern' },
      { icon: '$emailCustom', text: 'Email an Ansprechpartner' },
    ],

    appVersion: version,
    openFeedbackForm: false,
    feedbackFormHasLoaded: false,
    feedbackFormSubmitted: false,
    feedbackFormFrameKey: 0,
    feedbackButtonPulseTimeout: undefined,
    hasAutoOpenedFeedbackForm: false,
    feedbackFormUrl,
  }),
  computed: {
    ...mapGetters({ currentUser: userGetters.USER }),
    showFeedbackButton() {
      return !this.$store.state.mandantEditor.layoutMode;
    },
  },
  watch: {
    showFeedbackButton: {
      immediate: true,
      handler(showButton) {
        if (!showButton) {
          this.openFeedbackForm = false;
          this.stopFeedbackButtonPulse();
          return;
        }

        if (!this.hasAutoOpenedFeedbackForm && this.shouldAutoOpenFeedbackForm()) {
          this.hasAutoOpenedFeedbackForm = true;
          this.$nextTick(this.showFeedbackForm);
        }
      },
    },
  },
  beforeDestroy() {
    this.stopFeedbackButtonPulse();
  },
  methods: {
    showFeedbackForm() {
      this.feedbackFormHasLoaded = false;
      this.feedbackFormSubmitted = false;
      this.feedbackFormFrameKey += 1;
      this.openFeedbackForm = true;
    },
    wasFeedbackFormSubmitted() {
      try {
        return window.localStorage.getItem(feedbackFormSubmittedStorageKey) === 'true';
      } catch {
        return false;
      }
    },
    wasFeedbackFormDismissed() {
      try {
        return window.localStorage.getItem(feedbackFormDismissedStorageKey) === 'true';
      } catch {
        return false;
      }
    },
    shouldAutoOpenFeedbackForm() {
      return !this.wasFeedbackFormSubmitted() && !this.wasFeedbackFormDismissed();
    },
    markFeedbackFormSubmitted() {
      try {
        window.localStorage.setItem(feedbackFormSubmittedStorageKey, 'true');
      } catch {
        // The snackbar still confirms the submitted form if storage is unavailable.
      }
    },
    markFeedbackFormDismissed() {
      try {
        window.localStorage.setItem(feedbackFormDismissedStorageKey, 'true');
      } catch {
        // Dismissing the dialog should still close it if storage is unavailable.
      }
    },
    showSnackbarSuccess(message) {
      this.$store.commit(viewContentMutations.SHOW_SNACKBAR_SUCCESS, message);
    },
    getFeedbackButtonElement() {
      return this.$el.querySelector('#bbh-floating-btn-feedback');
    },
    stopFeedbackButtonPulse() {
      if (this.feedbackButtonPulseTimeout) {
        window.clearTimeout(this.feedbackButtonPulseTimeout);
        this.feedbackButtonPulseTimeout = undefined;
      }

      this.getFeedbackButtonElement()?.classList.remove(feedbackButtonPulseClass);
    },
    pulseFeedbackButton() {
      const feedbackButton = this.getFeedbackButtonElement();
      if (!feedbackButton) return;

      this.stopFeedbackButtonPulse();
      feedbackButton.classList.add(feedbackButtonPulseClass);
      this.feedbackButtonPulseTimeout = window.setTimeout(
        this.stopFeedbackButtonPulse,
        feedbackButtonPulseDuration
      );
    },
    handleFeedbackDialogDismiss() {
      this.markFeedbackFormDismissed();
      this.openFeedbackForm = false;

      if (!this.feedbackFormSubmitted) {
        this.pulseFeedbackButton();
      }
    },
    handleFeedbackFormLoad() {
      if (!this.openFeedbackForm || this.feedbackFormSubmitted) return;

      if (!this.feedbackFormHasLoaded) {
        this.feedbackFormHasLoaded = true;
        return;
      }

      this.feedbackFormSubmitted = true;
      this.markFeedbackFormSubmitted();
      this.openFeedbackForm = false;
      this.showSnackbarSuccess('Vielen Dank für Ihr Feedback.');
    },
    handleIconClicked(event) {
      switch (event) {
        case '$phoneAlt':
          window.open(
            'mailto:kontakt@bbh-solutions.de?subject=BBHS%2FAnrufbitte&body=Liebes%20BBHS-Team!%0D%0A%0D%0AIch%20bitte%20um%20Anruf%20unter%3A%20%0D%0A%5BBitte%20Telefonnummer%20angeben%5D.%20%0D%0A%0D%0AIch%20bin%20am%20besten%20erreichbar%3A%20%0D%0A%5BTag%2C%20Uhrzeit%20%E2%80%93%20Geben%20Sie%20uns%20dazu%20am%20besten%202%20oder%203%20Zeitfenster%20an%2C%20in%20denen%20wir%20Sie%20gut%20erreichen%20k%C3%B6nnen%5D%20%0D%0A%0D%0AMein%20Anliegen%3A%20%0D%0A%5BBitte%20schildern%20Sie%20Ihr%20Anliegen%20m%C3%B6glichst%20konkret%2C%20damit%20wir%20die%20richtigen%20Ansprechpartner*innen%20f%C3%BCr%20Sie%20finden.%5D'
          );
          break;
        case '$emailCustom':
          window.open(
            'mailto:kontakt@bbh-solutions.de?subject=BBHS%20Supportanfrage&body=Liebes%20BBHS-Team!%0D%0A%0D%0AMein%20Anliegen%3A%0D%0A%5BBitte%20schildern%20Sie%20Ihr%20Anliegen%20m%C3%B6glichst%20konkret.%5D'
          );
          break;

        default:
          break;
      }
    },

    consoleLogVersion() {
      console.log(
        `Version: %c${this.appVersion}`,
        'color: #007D55; font-weight: bold; font-size: 1rem'
      );
    },
  },
};
</script>

<style lang="scss">
#bbh-floating-btn,
#bbh-floating-btn-feedback {
  &.v-btn--fab.v-size--default {
    position: fixed;
    bottom: 35px;
    right: 35px;

    box-shadow: 2px 2px 9px rgba(0, 0, 0, 0.25);

    width: 50px;
    height: 50px;

    & .v-icon .v-icon__component {
      --bbh-icon-color: var(--bbh-white);
    }
  }
}

#bbh-floating-btn-feedback.bbh-floating-btn-feedback {
  &.v-btn--fab.v-size--default {
    bottom: 95px;
    right: 35px;
    width: 50px;
    background-color: #5799ad !important;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.26);
  }

  &.bbh-floating-btn-feedback--pulse.v-btn--fab.v-size--default {
    animation: bbh-feedback-button-pulse 2.4s ease-out infinite;
  }
}

.bbh-floating-btn-feedback-icon {
  position: relative;
  display: block;
  width: 41px;
  height: 41px;
}

.bbh-floating-btn-feedback-icon__person {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 28px;
  height: 21px;
  border: 3px solid var(--bbh-white);
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  border-bottom: 0;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: -24px;
    width: 15px;
    height: 15px;
    border: 3px solid var(--bbh-white);
    border-radius: 50%;
    background: #5799ad;
  }
}

.bbh-floating-btn-feedback-icon__bubble {
  position: absolute;
  top: 0;
  right: 0;
  width: 28px;
  height: 21px;
  border: 3px solid var(--bbh-white);

  &::after {
    content: '';
    position: absolute;
    right: 4px;
    bottom: -9px;
    width: 11px;
    height: 11px;
    border-right: 3px solid var(--bbh-white);
    border-bottom: 3px solid var(--bbh-white);
    background: #5799ad;
    transform: skewY(-35deg);
  }
}

.template-editor-feedback-dialog.bbh-dialog.v-dialog {
  position: relative;
  overflow: visible;
  max-width: min(720px, calc(100vw - 96px));

  &::after {
    content: '';
    position: absolute;
    right: 46px;
    bottom: -18px;
    width: 36px;
    height: 36px;
    background: var(--bbh-white);
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    transform: rotate(45deg);
  }

  .dialogText {
    overflow: visible;
  }
}

.template-editor-feedback-content {
  display: grid;
  gap: 18px;
  width: 100%;
}

.template-editor-feedback-intro {
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  color: var(--bbh-black);
  line-height: 1.45;
}

.template-editor-feedback-button-preview {
  display: grid;
  place-items: center;
  width: 66px;
  height: 66px;
  border-radius: 50%;
  background: #5799ad;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.22);
}

.template-editor-feedback-form {
  width: 640px;
  max-width: 100%;
  height: min(58vh, 520px);
  min-height: 420px;
  border: none;
}

@keyframes bbh-feedback-button-pulse {
  0% {
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.26),
      0 0 0 0 rgba(0, 88, 160, 0.42);
  }

  70% {
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.26),
      0 0 0 14px rgba(0, 88, 160, 0);
  }

  100% {
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.26),
      0 0 0 0 rgba(0, 88, 160, 0);
  }
}

@media (max-width: 720px) {
  .template-editor-feedback-dialog.bbh-dialog.v-dialog {
    max-width: calc(100vw - 32px);

    &::after {
      right: 38px;
    }
  }

  .template-editor-feedback-intro {
    grid-template-columns: 50px minmax(0, 1fr);
    gap: 12px;
  }

  .template-editor-feedback-button-preview {
    width: 50px;
    height: 50px;

    .bbh-floating-btn-feedback-icon {
      transform: scale(0.76);
    }
  }

  .template-editor-feedback-form {
    min-height: 360px;
  }
}

.bbh-floating-btn-menu-wrapper.v-menu__content {
  transform: translate(-34px, 17px);

  width: max-content;
  padding: 5px 0;

  & #bbh-floating-btn-menu {
    display: grid;
    grid-template-rows: 50px 50px;
    grid-template-columns: 45px;
    align-items: center;
    align-content: center;
    justify-items: center;
    justify-content: center;

    min-width: unset;
    width: 100%;

    margin: 0;

    & .v-icon .v-icon__component {
      --bbh-icon-color: var(--bbh-black);
      width: 23px;
      height: 23px;
    }
  }
}
</style>
